import { useState } from "react";
import { format } from "date-fns";
import { buildInitialCorrections } from "../mock/correctionsMock";

const normalize = (value) => (value === "" || value === undefined ? null : value);

/**
 * Редактирование слоя корректировок.
 * saved — сохранённые корректировки { [rowId]: { fields, author, date } },
 * draft — несохранённые изменения { [rowId]: { [field]: value | null } }, null = убрать корректировку поля.
 */
function useCorrectionsEditor() {
  const [saved, setSaved] = useState(buildInitialCorrections);
  const [draft, setDraft] = useState({});
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);

  const getSavedValue = (rowId, field) => normalize(saved[rowId]?.fields?.[field]);

  const isDirty = (rowId, field) =>
    Boolean(draft[rowId] && Object.prototype.hasOwnProperty.call(draft[rowId], field));

  const getValue = (rowId, field) =>
    isDirty(rowId, field) ? draft[rowId][field] : getSavedValue(rowId, field);

  /** Итоговые поля корректировки строки с учётом черновика */
  const getEffectiveFields = (rowId) => {
    const fields = { ...(saved[rowId]?.fields || {}) };
    Object.entries(draft[rowId] || {}).forEach(([field, value]) => {
      if (value == null) delete fields[field];
      else fields[field] = value;
    });
    return fields;
  };

  const pushHistory = (nextDraft) => {
    setPast((prev) => [...prev, draft]);
    setFuture([]);
    setDraft(nextDraft);
  };

  const setValue = (rowId, field, rawValue) => {
    const value = normalize(rawValue);
    if (value === getValue(rowId, field)) return;

    const rowDraft = { ...(draft[rowId] || {}) };
    if (value === getSavedValue(rowId, field)) delete rowDraft[field];
    else rowDraft[field] = value;

    const nextDraft = { ...draft };
    if (Object.keys(rowDraft).length) nextDraft[rowId] = rowDraft;
    else delete nextDraft[rowId];
    pushHistory(nextDraft);
  };

  const undo = () => {
    if (!past.length) return;
    setFuture((prev) => [draft, ...prev]);
    setDraft(past[past.length - 1]);
    setPast((prev) => prev.slice(0, -1));
  };

  const redo = () => {
    if (!future.length) return;
    setPast((prev) => [...prev, draft]);
    setDraft(future[0]);
    setFuture((prev) => prev.slice(1));
  };

  const discard = () => {
    if (Object.keys(draft).length) pushHistory({});
  };

  const save = (author) => {
    const date = format(new Date(), "dd.MM.yyyy");
    setSaved((prev) => {
      const next = { ...prev };
      Object.entries(draft).forEach(([rowId, changes]) => {
        const fields = { ...(prev[rowId]?.fields || {}) };
        Object.entries(changes).forEach(([field, value]) => {
          if (value == null) delete fields[field];
          else fields[field] = value;
        });
        if (Object.keys(fields).length) next[rowId] = { fields, author, date };
        else delete next[rowId];
      });
      return next;
    });
    setDraft({});
    setPast([]);
    setFuture([]);
  };

  /** Сохранение корректировки одной строки из карточки операции, минуя черновик таблицы */
  const commitRow = (rowId, fields, author) => {
    const date = format(new Date(), "dd.MM.yyyy");
    const dropRow = (state) => {
      if (!state[rowId]) return state;
      const next = { ...state };
      delete next[rowId];
      return next;
    };

    setSaved((prev) => {
      const next = { ...prev };
      if (Object.keys(fields).length) next[rowId] = { fields, author, date };
      else delete next[rowId];
      return next;
    });
    setDraft(dropRow);
    setPast((prev) => prev.map(dropRow));
    setFuture((prev) => prev.map(dropRow));
  };

  const changesCount = Object.values(draft).reduce(
    (sum, rowDraft) => sum + Object.keys(rowDraft).length,
    0
  );

  return {
    getValue,
    getEffectiveFields,
    getRowCorrection: (rowId) => saved[rowId] || null,
    hasRowChanges: (rowId) => Boolean(draft[rowId]),
    isDirty,
    setValue,
    undo,
    redo,
    discard,
    save,
    commitRow,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    changesCount,
  };
}

export default useCorrectionsEditor;
